import DetailSpec from "./DetailSpec";
import DetailInfo from "./DetailInfo";
import Annotation from "@/map/element/Annotation";
import GPSUtils from "@/utils/GPSUtils";
import LangUtils from "@/utils/LangUtils";

export default class MapElementDetailSpec extends DetailSpec {
    constructor(mapElement, mapBundleId) {
        super();

        this.mapElement = mapElement;
        this.mapBundleId = mapBundleId;
    }

    getId() {
        throw new Error("method must be overriden by subclasses.");
    }

    getMapBundleId() {
        return this.mapBundleId;
    }

    getName() {
        return this.mapElement ? this.mapElement.getName() : null;
    }

    getSummary() {
        return this.mapElement ? this.mapElement.getSummary() : null;
    }

    getDescription() {
        return this.mapElement ? this.mapElement.getDescription() : null;
    }

    getInfos(i18n, mapService, mapBundleId) {
        if (this.mapElement) {
            const detailInfos = [];

            const tabs = this.mapElement.getTabs();
            if (tabs) {
                for (const tab of tabs) {
                    if (tab.type == "photo") {
                        const photosUrls = [];
                        if (tab.parameters || tab.parameters.photos) {
                            for (const photo of tab.parameters.photos) {
                                photosUrls.push(mapService.buildUrlToAsset(mapBundleId, LangUtils.valueForLanguage(photo.url, i18n.locale)));
                            }
                        }
                        detailInfos.push(new DetailInfo("photos", "images", null, photosUrls));
                    }
                }
            }

            const trailInfo = this.mapElement.getTrailInformation();
            if (trailInfo) {
				if (trailInfo.elevationGain && trailInfo.elevationGain != 0.0) {
					detailInfos.push(new DetailInfo("elevationGain", "elevation", i18n.t("map_detail.detail_panel.trail_information.elevation_gain"), trailInfo.elevationGain));
				}
				if (trailInfo.elevationLoss && trailInfo.elevationLoss != 0.0) {
					detailInfos.push(new DetailInfo("elevationLoss", "elevation", i18n.t("map_detail.detail_panel.trail_information.elevation_loss"), trailInfo.elevationLoss));
				}
				if (trailInfo.length != 0.0) {
					detailInfos.push(new DetailInfo("length", "distance", i18n.t("map_detail.detail_panel.trail_information.length"), trailInfo.length));
				}
				if (trailInfo.difficulty != null) {
                    detailInfos.push(new DetailInfo("difficulty", "string", i18n.t("map_detail.detail_panel.trail_information.difficulty"), LangUtils.valueForLanguage(trailInfo.difficulty.label, i18n.locale)));
				}
				if (trailInfo.direction != null) {
					detailInfos.push(new DetailInfo("direction", "string", i18n.t("map_detail.detail_panel.trail_information.direction"), LangUtils.valueForLanguage(trailInfo.direction.label, i18n.locale)));
				}
				if (trailInfo.type != null) {
					detailInfos.push(new DetailInfo("type", "string", i18n.t("map_detail.detail_panel.trail_information.type"), LangUtils.valueForLanguage(trailInfo.type.label, i18n.locale)));
				}
            }

            const placeInfo = this.mapElement.getPlaceInformation();
            if (placeInfo) {
                if (placeInfo.location) {
					const roundedLatitude = Math.round(placeInfo.location.latitude * 10000000.0) / 10000000.0;
					const roundedLongitude = Math.round(placeInfo.location.longitude * 10000000.0) / 10000000.0;
					detailInfos.push(new DetailInfo("location", "string", i18n.t("map_detail.detail_panel.place_information.location"), roundedLatitude + ", " + roundedLongitude));
				}
				if (placeInfo.address != null) {
					detailInfos.push(new DetailInfo("address", "string", i18n.t("map_detail.detail_panel.place_information.address"), LangUtils.valueForLanguage(placeInfo.address, i18n.locale)));
				}
				if (placeInfo.url != null) {
					detailInfos.push(new DetailInfo("webSite", "url", i18n.t("map_detail.detail_panel.place_information.web_site"), placeInfo.url));
				}
				if (placeInfo.phone != null) {
					detailInfos.push(new DetailInfo("phone", "phone", i18n.t("map_detail.detail_panel.place_information.phone"), placeInfo.phone));
				}
				if (placeInfo.phoneAlternate != null) {
					detailInfos.push(new DetailInfo("phoneAlternate", "phone", i18n.t("map_detail.detail_panel.place_information.phone_alternate"), placeInfo.phoneAlternate));
				}
				if (placeInfo.phoneTollFree != null) {
					detailInfos.push(new DetailInfo("phoneTollFree", "phone", i18n.t("map_detail.detail_panel.place_information.phone_toll_free"), placeInfo.phoneTollFree));
				}
				if (placeInfo.email != null) {
					detailInfos.push(new DetailInfo("email", "email", i18n.t("map_detail.detail_panel.place_information.email"), placeInfo.email));
				}
				if (placeInfo.accessible) {
					detailInfos.push(new DetailInfo("accessible", "string", i18n.t("map_detail.detail_panel.place_information.accessible"), i18n.t("map_detail.detail_panel.place_information.yes")));
				}
				if (placeInfo.facebook != null) {
					detailInfos.push(new DetailInfo("facebook", "url", i18n.t("map_detail.detail_panel.place_information.facebook"), placeInfo.facebook));
				}
				if (placeInfo.twitter != null) {
					detailInfos.push(new DetailInfo("twitter", "url", i18n.t("map_detail.detail_panel.place_information.twitter"), placeInfo.twitter));
				}
				if (placeInfo.linkedIn != null) {
					detailInfos.push(new DetailInfo("linkedin", "url", i18n.t("map_detail.detail_panel.place_information.linkedIn"), placeInfo.linkedIn));
				}
            }

            return detailInfos;
        } else {
            return null;
        }
    }

    isCoordinatesVisible() {
        return this.mapElement instanceof Annotation && this.mapElement.getLocation();
    }

    isLinkToGoogleMapsVisible() {
        return this.mapElement instanceof Annotation && this.mapElement.getLocation();
    }

    getCoordinatesLatitude() {
        if (this.mapElement.getLocation()) {
            return GPSUtils.getDMS(this.mapElement.getLocation().latitude, "lat");
        } else {
            return null;
        }
    }

    getCoordinatesLongitude() {
        if (this.mapElement.getLocation()) {
            return GPSUtils.getDMS(this.mapElement.getLocation().longitude, "long");
        } else {
            return null;
        }
    }

    getLinkToGoogleMaps() {
        return "https://www.google.com/maps/dir/?api=1&destination=" + this.mapElement.getLocation().latitude + "," + this.mapElement.getLocation().longitude;
    }

    getHeaderIconUrl() {
        return this.mapElement.getIconFileName();
    }

}